import axios, { AxiosProgressEvent, AxiosResponse } from 'axios';
import {
  sessionUrl,
  STATUS_CODE_FORBIDDEN,
  STATUS_CODE_UNAUTHORIZED,
  userUrl,
  fileUrl,
  profileUrl,
} from '@/common/constants';
import { primaryDomain } from '@/config';
import TokenService from '@/services/TokenService';
import { logout } from '@/common/utils';
import router from '@/router';
import { FileUploadResponse } from '@/types/FileUploadResponse';
import { GeneralInfo } from '@/types/GeneralInfo';
import { RetrievalRequestMessage } from '@/types/RetrievalRequestMessage';
import { GeneratorRequestMessage } from '@/types/GeneratorRequestMessage';
import { OcrReprocessRequest } from '@/types/OcrReprocessRequest';
import { User } from '@/types/User';
import { MatterSubmitResponse } from '@/types/MatterSubmitResponse';
import { CreateSessionResponse } from '@/types/CreateSessionResponse';
import { Session } from '@/types/Session';
import { GetSessionsResponse } from '@/types/GetSessionsResponse';
import { ValidateTokenResponse } from '@/types/ValidateTokenResponse';
import { LoginResponse } from '@/types/LoginResponse';

const apiClient = axios.create({
  baseURL: primaryDomain,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = TokenService.getToken();
    if (token) {
      config.headers['Token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response) {
      if (error.response.status === STATUS_CODE_UNAUTHORIZED) {
        console.error('Unauthorized access - redirecting to login...');
        await logout(router);
      } else if (error.response.status === STATUS_CODE_FORBIDDEN) {
        console.error('Access denied!');
        await router.push('/user-profile?page=1');
      }
    }
    return Promise.reject(error);
  },
);

export const login = async (
  email: string,
  password: string,
): Promise<AxiosResponse<LoginResponse>> => {
  return apiClient.post('/token/create', { email, password });
};

export const getUser = async (): Promise<AxiosResponse<User>> => {
  return apiClient.get<User>(profileUrl);
};

export const createUser = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  isAdmin: boolean,
): Promise<AxiosResponse<void>> => {
  return apiClient.post(`${userUrl}/create`, {
    email,
    password,
    firstName,
    lastName,
    isAdmin,
  });
};

export const getSessions = async (params: {
  offset: number;
  limit: number;
}): Promise<AxiosResponse<GetSessionsResponse>> => {
  return apiClient.get(
    `${sessionUrl}/all?offset=${params.offset}&limit=${params.limit}`,
  );
};

export const getUsers = async (): Promise<AxiosResponse<User[]>> => {
  return apiClient.get(`${userUrl}/all`);
};

export const updateCredentials = async (
  email: string,
  password: string,
): Promise<AxiosResponse<void>> => {
  return apiClient.patch(`${profileUrl}/credentials`, { email, password });
};

export const updateEmail = async (
  uid: number,
  email: string,
): Promise<AxiosResponse<void>> => {
  return apiClient.patch(`${userUrl}/${uid}/email`, { email });
};

export const updatePassword = async (
  uid: number,
  password: string,
): Promise<AxiosResponse<void>> => {
  return apiClient.patch(`${userUrl}/${uid}/password`, { password });
};

export const deleteUser = async (uid: number): Promise<AxiosResponse<void>> => {
  return apiClient.delete(`${userUrl}/${uid}`);
};

export const createSession = async (
  generalInfo: GeneralInfo,
): Promise<AxiosResponse<CreateSessionResponse>> => {
  return apiClient.post(`${sessionUrl}/create`, generalInfo);
};

export const getSessionById = async (
  id: number,
): Promise<AxiosResponse<Session>> => {
  return apiClient.get(`${sessionUrl}/${id}`);
};

export const deleteSession = async (
  sessionId: number,
): Promise<AxiosResponse<void>> => {
  return apiClient.delete(`${sessionUrl}/${sessionId}`);
};

export const submitMatterName = async (
  matterName: string,
): Promise<AxiosResponse<MatterSubmitResponse>> => {
  return apiClient.get(`/matter/${matterName}`);
};

export const uploadFile = (
  data: FormData,
  onProgress: (progress: number) => void,
): {
  request: Promise<AxiosResponse<FileUploadResponse>>;
  abortController: AbortController;
} => {
  const abortController = new AbortController();

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    signal: abortController.signal,
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      if (progressEvent.total) {
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100,
        );
        onProgress(progress);
      }
    },
  };

  const request = apiClient.post<FileUploadResponse>(
    `${fileUrl}/upload`,
    data,
    config,
  );

  return { request, abortController };
};

export const renameFile = (
  fileId: number,
  newName: string,
): Promise<AxiosResponse<void>> => {
  return apiClient.patch(`${fileUrl}/${fileId}/rename`, { newName });
};

export const processFiles = (
  sessionId: number,
  fileIds: number[],
): Promise<AxiosResponse<void>> => {
  return apiClient.post(`${sessionUrl}/${sessionId}/process-files`, {
    fileIds,
  });
};

export const reprocessOcr = async (
  ocrReprocessRequest: OcrReprocessRequest,
  sessionId: number,
): Promise<AxiosResponse<void>> => {
  return apiClient.post(`${sessionUrl}/${sessionId}/ocr`, ocrReprocessRequest);
};

export const reprocessRetrieval = async (
  retrievalRequestMessage: RetrievalRequestMessage,
): Promise<AxiosResponse<void>> => {
  return apiClient.post(
    `${sessionUrl}/${retrievalRequestMessage.id}/retrieval`,
    retrievalRequestMessage,
  );
};

export const reprocessGenerator = async (
  generatorRequestMessage: GeneratorRequestMessage,
): Promise<AxiosResponse<void>> => {
  return apiClient.post(
    `${sessionUrl}/${generatorRequestMessage.id}/generator`,
    generatorRequestMessage,
  );
};

export const validateToken = async (
  token: string,
): Promise<AxiosResponse<ValidateTokenResponse>> => {
  return apiClient.post('/token/validate', { token });
};
